import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Sparkles } from 'lucide-react';
import { fetchBySlug } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function NyhedDetaljeDe() {
  const { slug } = useParams();
  const [nyhed, setNyhed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const data = await fetchBySlug(slug, 'de');
      setNyhed(data);
      setLoading(false);
    }
    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-900">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0E67A2] border-t-transparent" />
      </div>
    );
  }

  if (!nyhed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 text-slate-900 px-4 text-center">
        <SEO title="Artikel nicht gefunden" description="Der gewünschte Artikel existiert leider nicht." path={`/de/neuigkeiten/${slug}`} lang="de" />
        <h1 className="text-3xl font-bold">Artikel nicht gefunden</h1>
        <p className="mt-2 text-slate-500">Der Beitrag, den Sie suchen, existiert leider nicht oder wurde verschoben.</p>
        <Link to="/de/neuigkeiten" className="mt-6 rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white transition hover:bg-[#0E67A2]/90">
          Zurück zu den Neuigkeiten
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: nyhed.title,
    datePublished: nyhed.date,
    author: {
      '@type': 'Organization',
      name: 'Danshaker',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Danshaker',
      logo: {
        '@type': 'ImageObject',
        url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/14d0f8cb-56b7-4a51-8379-6cfdf86af873.png',
      },
    },
    description: nyhed.content ? nyhed.content.replace(/<[^>]*>?/gm, '').substring(0, 160) : '',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased pb-20">
      <SEO
        title={nyhed.title}
        description={nyhed.content ? nyhed.content.replace(/<[^>]*>?/gm, '').substring(0, 160) : 'Neuigkeiten von Danshaker'}
        path={`/de/neuigkeiten/${slug}`}
        schema={articleSchema}
        lang="de"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Article Content */}
      <article className="mx-auto max-w-4xl px-4 pt-16 md:px-8 md:pt-24">
        {/* Back link */}
        <Link
          to="/de/neuigkeiten"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-[#0E67A2] mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück zur Übersicht
        </Link>

        {/* Header */}
        <div className="space-y-4 border-b border-slate-200 pb-8">
          <div className="flex items-center gap-2 text-xs font-bold text-[#0E67A2] bg-[#0E67A2]/10 w-fit px-3 py-1 rounded-full">
            <Sparkles className="h-3 w-3" />
            <span>Neuigkeiten</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl md:text-5xl leading-tight">
            {nyhed.title}
          </h1>
          <div className="flex items-center gap-2 text-sm text-slate-500 font-medium pt-2">
            <Calendar className="h-4 w-4" />
            <time>{nyhed.date}</time>
          </div>
        </div>

        {/* Body content */}
        <div className="py-10">
          <div
            className="prose prose-slate max-w-none prose-lg text-slate-700 leading-relaxed font-medium prose-p:mb-6 prose-p:text-lg"
            dangerouslySetInnerHTML={{ __html: nyhed.content }}
          />
        </div>

        {/* CTA Banner bottom */}
        <div className="rounded-3xl bg-slate-900 p-8 text-white mt-8 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl font-bold">Möchten Sie den Danshaker live erleben?</h3>
            <p className="text-sm text-slate-300">Buchen Sie eine unverbindliche Vorführung direkt in Ihrem eigenen Stall.</p>
          </div>
          <Link
            to="/de/kostenlose-demo"
            className="rounded-xl bg-[#0E67A2] px-6 py-3.5 font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md shrink-0"
          >
            Demo buchen
          </Link>
        </div>
      </article>
    </div>
  );
}
